import axios from 'axios';
import { getRefreshToken, setTokens, clearTokens } from './token-store';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

/** Shape C — `/api/auth/refresh` returns a raw map with these fixed keys (CONTRACTS.md §12 / doc 02 §5.2). */
interface TokenRefreshResponseDTO {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresIn?: number;
  tokenType?: string;
}

let inFlight: Promise<string> | null = null;

/**
 * Single-flight refresh: concurrent callers (401 retries in api/http.ts AND the proactive pre-expiry
 * timer in AuthProvider) share ONE network call. Port of AuthSessionCoordinator.refreshTokensSingleFlight
 * (doc 03 §4.5). Resolves to the fresh access token.
 */
export function refreshSingleFlight(): Promise<string> {
  inFlight ??= doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doRefresh(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('no_refresh_token');
  // /api/auth/refresh is permitAll (SecurityConfig) -> bare axios: no Bearer, no response-interceptor recursion.
  const { data } = await axios.post<TokenRefreshResponseDTO>(`${API_BASE}api/auth/refresh`, {
    refreshToken,
  });
  // patchTokens preserves identity (roles/email/name) — refresh never returns them (doc 03 §2.2, §4.3).
  // Keep the old refresh token when the server omits a new one (wasm parity).
  setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken ?? refreshToken });
  return data.accessToken;
}

/** Refresh failed / no refresh token -> clear session; <RequireAuth> then redirects to /login (doc 03 §4.5). */
export function onRefreshFailed(): void {
  clearTokens();
}
